/**
 * NEXUS UI - SearchInput
 * Futuristic search input with suggestions dropdown
 */

import React, { useState, useRef, useEffect } from 'react';
import type { BaseProps, NexusVariant, NexusSize } from './types';

interface SearchSuggestion {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

export interface SearchInputProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  suggestions?: SearchSuggestion[];
  onSuggestionSelect?: (suggestion: SearchSuggestion) => void;
  variant?: NexusVariant;
  size?: NexusSize;
  loading?: boolean;
  disabled?: boolean;
  clearable?: boolean;
}

const variantColors: Record<NexusVariant, { base: string; glow: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const sizeConfig: Record<NexusSize, { height: number; fontSize: string; padding: string; iconSize: number }> = {
  sm: { height: 32, fontSize: '12px', padding: '8px 12px', iconSize: 14 },
  md: { height: 40, fontSize: '14px', padding: '10px 14px', iconSize: 16 },
  lg: { height: 48, fontSize: '16px', padding: '12px 16px', iconSize: 18 },
  xl: { height: 56, fontSize: '18px', padding: '14px 18px', iconSize: 20 },
};

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Search...',
  suggestions = [],
  onSuggestionSelect,
  variant = 'cyan',
  size = 'md',
  loading = false,
  disabled = false,
  clearable = true,
  className = '',
  style,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const colors = variantColors[variant];
  const config = sizeConfig[size];

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show suggestions when focused and there are suggestions
  useEffect(() => {
    if (isFocused && suggestions.length > 0 && value) {
      setShowSuggestions(true);
    }
  }, [isFocused, suggestions.length, value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) {
      if (e.key === 'Enter' && onSearch) {
        onSearch(value);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else if (onSearch) {
          onSearch(value);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    onChange(suggestion.label);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onSuggestionSelect?.(suggestion);
    inputRef.current?.focus();
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div
      ref={containerRef}
      className={`nx-search-input ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        ...style,
      }}
    >
      {/* Input container */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          height: config.height,
          background: 'var(--nx-void-panel)',
          border: `1px solid ${isFocused ? colors.base : 'rgba(255, 255, 255, 0.1)'}`,
          borderRadius: '6px',
          transition: 'all 0.15s ease-out',
          boxShadow: isFocused ? `0 0 12px ${colors.glow}, inset 0 0 8px ${colors.glow}` : 'none',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {/* Search icon */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: config.height,
            color: isFocused ? colors.base : 'var(--nx-text-tertiary)',
            transition: 'color 0.15s ease-out',
          }}
        >
          {loading ? (
            <div
              style={{
                width: config.iconSize,
                height: config.iconSize,
                border: `2px solid ${colors.base}30`,
                borderTopColor: colors.base,
                borderRadius: '50%',
                animation: 'nx-rotate 0.8s linear infinite',
              }}
            />
          ) : (
            <svg
              width={config.iconSize}
              height={config.iconSize}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          )}
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            flex: 1,
            height: '100%',
            padding: 0,
            paddingRight: config.padding,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontFamily: 'var(--nx-font-interface)',
            fontSize: config.fontSize,
            color: 'var(--nx-text-primary)',
          }}
        />

        {/* Clear button */}
        {clearable && value && !disabled && (
          <button
            onClick={handleClear}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: config.height - 8,
              height: config.height - 8,
              marginRight: 4,
              background: 'transparent',
              border: 'none',
              borderRadius: '4px',
              color: 'var(--nx-text-tertiary)',
              cursor: 'pointer',
              transition: 'all 0.1s ease-out',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = 'var(--nx-text-primary)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--nx-text-tertiary)';
            }}
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            background: 'var(--nx-void-panel)',
            border: `1px solid ${colors.base}50`,
            borderRadius: '6px',
            boxShadow: `0 4px 20px rgba(0, 0, 0, 0.4), 0 0 12px ${colors.glow}`,
            overflow: 'hidden',
            zIndex: 100,
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: config.padding,
                cursor: 'pointer',
                background: selectedIndex === index ? `${colors.base}20` : 'transparent',
                borderBottom: index < suggestions.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                transition: 'background 0.1s ease-out',
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {suggestion.icon && (
                <span style={{ color: colors.base, opacity: 0.8 }}>
                  {suggestion.icon}
                </span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: 'var(--nx-font-interface)',
                    fontSize: config.fontSize,
                    color: 'var(--nx-text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {suggestion.label}
                </div>
                {suggestion.description && (
                  <div
                    style={{
                      fontFamily: 'var(--nx-font-interface)',
                      fontSize: '11px',
                      color: 'var(--nx-text-tertiary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginTop: '2px',
                    }}
                  >
                    {suggestion.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchInput;
